class YumiPlushParade extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.panels = [...this.querySelectorAll('[data-yp-panel]')];
    this.originals ||= new Map();
    this.catalog = new Map();
    const pajamas = [...(this.querySelector('[data-yp-pajamas]')?.content.querySelectorAll('a') || [])];
    for (let i = pajamas.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pajamas[i], pajamas[j]] = [pajamas[j], pajamas[i]];
    }
    for (const panel of this.panels) {
      const group = panel.querySelector('[data-yp-original]');
      if (!this.originals.has(panel) && group) {
        this.originals.set(panel, [...group.children].map((node, index) => {
          const pajama = index % 3 === 1 ? pajamas[Math.floor(index / 3) % pajamas.length] : null;
          return (pajama || node).cloneNode(true);
        }));
      }
      try {
        for (const item of JSON.parse(panel.querySelector('[data-yp-catalog]').textContent)) this.catalog.set(String(item.id), item);
      } catch { /* Preserve product links if configuration is unavailable. */ }
    }
    const { signal } = this.controller;
    this.addEventListener('click', event => this.onClick(event), { signal });
    for (const panel of this.panels) {
      if (!panel.classList.contains('yp-panel--mobile')) continue;
      const stage = panel.querySelector('[data-yp-stage]');
      const track = stage.querySelector('[data-yp-track]');
      let resumeTimer, touching = false;
      const scheduleResume = () => {
        clearTimeout(resumeTimer);
        if (touching || !panel.hasAttribute('data-manual-scroll')) return;
        resumeTimer = setTimeout(() => {
          if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
          if (panel.hasAttribute('data-modal-open') || stage.querySelector(':focus-visible')) {
            scheduleResume();
            return;
          }
          const width = track.firstElementChild?.getBoundingClientRect().width;
          if (!width) return;
          const position = ((stage.scrollLeft % width) + width) % width;
          const style = getComputedStyle(panel);
          const duration = parseFloat(style.getPropertyValue('--yp-duration')) || 40;
          const reverse = style.getPropertyValue('--yp-direction').trim() === 'reverse';
          const progress = reverse ? (1 - position / width) % 1 : position / width;
          track.style.animationDelay = `${-progress * duration}s`;
          stage.scrollLeft = 0;
          panel.removeAttribute('data-manual-scroll');
        }, 1000);
      };
      signal.addEventListener('abort', () => clearTimeout(resumeTimer), { once: true });
      const takeControl = () => {
        clearTimeout(resumeTimer);
        if (panel.hasAttribute('data-manual-scroll')) return;
        const transform = getComputedStyle(stage.querySelector('[data-yp-track]')).transform;
        const offset = transform === 'none' ? 0 : -new DOMMatrixReadOnly(transform).m41;
        panel.setAttribute('data-manual-scroll', '');
        stage.scrollLeft += offset;
      };
      stage.addEventListener('pointerdown', event => {
        touching = true;
        takeControl();
        this.swipeStart = { x: event.clientX, y: event.clientY };
        this.swipeMoved = false;
      }, { signal, passive: true });
      stage.addEventListener('pointermove', event => {
        if (this.swipeStart && Math.hypot(event.clientX - this.swipeStart.x, event.clientY - this.swipeStart.y) > 8) this.swipeMoved = true;
      }, { signal, passive: true });
      const release = () => { touching = false; this.swipeStart = null; scheduleResume(); };
      window.addEventListener('pointerup', release, { signal, passive: true });
      stage.addEventListener('pointercancel', () => { this.swipeMoved = true; release(); }, { signal, passive: true });
      stage.addEventListener('wheel', () => { takeControl(); scheduleResume(); }, { signal, passive: true });
      stage.addEventListener('scroll', scheduleResume, { signal, passive: true });
    }

    const dialogs = [this.querySelector('[data-yumi-gallery]'), this.closest('yumi-pajama-bundle')?.querySelector('[data-builder]')];
    dialogs.filter(Boolean).forEach(dialog => dialog.addEventListener('close', () => this.panels.forEach(p => p.removeAttribute('data-modal-open')), { signal }));
    this.observer = new ResizeObserver(() => this.resize());
    this.panels.forEach(panel => this.observer.observe(panel.querySelector('[data-yp-stage]')));
    this.resize();
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; this.observer?.disconnect(); }
  resize() {
    for (const panel of this.panels) {
      const stage = panel.querySelector('[data-yp-stage]');
      if (!stage.clientWidth) continue;
      const originals = this.originals.get(panel);
      if (!originals.length) continue;
      const style = getComputedStyle(panel);
      const size = parseFloat(style.getPropertyValue('--yp-size')) || 120;
      const gap = size * Number(style.getPropertyValue('--yp-gap')) / 100;
      const copies = Math.max(1, Math.ceil((stage.clientWidth + size) / (originals.length * (size * .82 + gap))));
      const key = `${copies}-${size}-${gap}`;
      const track = panel.querySelector('[data-yp-track]');
      if (track.dataset.layout !== key) {
        track.dataset.layout = key;
        const first = document.createElement('div');
        first.className = 'yp-group';
        for (let c = 0; c < copies; c++) {
          originals.forEach(node => {
            const item = node.cloneNode(true);
            if (c) { item.tabIndex = -1; item.setAttribute('aria-hidden', 'true'); }
            first.append(item);
          });
        }
        const second = first.cloneNode(true);
        second.setAttribute('aria-hidden', 'true');
        second.querySelectorAll('a').forEach(a => { a.tabIndex = -1; });
        track.replaceChildren(first, second);
      }
      const width = track.firstElementChild.getBoundingClientRect().width;
      panel.style.setProperty('--yp-duration', `${width / (Number(style.getPropertyValue('--yp-speed')) || 26)}s`);
    }
  }
  async onClick(event) {
    const panel = event.target.closest('[data-yp-panel]');
    if (!panel) return;
    const productLink = event.target.closest('[data-yp-product]');
    const cta = event.target.closest('[data-yp-open]');
    if (!cta && !productLink) return;
    if (productLink && this.swipeMoved && event.detail !== 0) { event.preventDefault(); this.swipeMoved = false; return; }
    if (productLink && (productLink.hasAttribute('data-yp-direct') || panel.dataset.action === 'product' || event.metaKey || event.ctrlKey || event.shiftKey)) return;
    const pajama = this.closest('yumi-pajama-bundle');
    if (pajama && productLink) return;
    event.preventDefault();
    if (cta && this.hasAttribute('data-product-parade')) {
      await customElements.whenDefined('yumi-pajama-bundle');
      let builder = this.closest('yumi-pajama-bundle') || this.querySelector('yumi-pajama-bundle[data-standalone]');
      if (!builder) {
        const template = this.querySelector('[data-yp-clothing-selector]');
        if (template) this.append(template.content.cloneNode(true));
        builder = this.querySelector('yumi-pajama-bundle[data-standalone]');
      }
      if (builder?.dataset.ready === 'true') {
        this.panels.forEach(p => p.setAttribute('data-modal-open', ''));
        builder.querySelector('[data-builder]').addEventListener('close', () => this.panels.forEach(p => p.removeAttribute('data-modal-open')), { once: true });
        builder.openBuilder();
        return;
      }
      panel.querySelector('[data-yp-message]').textContent = this.dataset.unavailable;
      return;
    }
    await customElements.whenDefined('yumi-bundle');
    const chooser = this.querySelector('yumi-bundle');
    const raw = productLink ? this.catalog.get(productLink.dataset.ypProduct) : null;
    if (!chooser?.openFromParade || !chooser.openFromParade(raw, panel.dataset.offer)) {
      panel.querySelector('[data-yp-message]').textContent = this.dataset.unavailable;
      if (productLink) location.assign(productLink.href);
      return;
    }
    panel.querySelector('[data-yp-message]').textContent = '';
  }
}
if (!customElements.get('yumi-plush-parade')) customElements.define('yumi-plush-parade', YumiPlushParade);
